import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Pencil } from 'lucide-react';
import Swal from 'sweetalert2';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import Modal from '../../components/common/Modal.jsx';
import { formatMoney, formatDate, today, daysAgo, primero, useLocalTable } from '../finanzas-cxp/shared.js';
import { exportTablePdf, buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';

/**
 * Listado + captura compartidos por Devoluciones y Devoluciones Inversa. Réplica de
 * V1 (`Admin/Devoluciones/ListadoDevoluciones.jsx` + `DevolucionesCreate.jsx` y
 * `Admin/DevolucionesInversa/ListadoDevolucionesInversa.jsx` + `DevolucionesInversaCreate.jsx`):
 *   - Listado "Reporte de Devoluciones": rango Fecha Inicio/Fin (default 30 días),
 *     filtro Cliente, buscador, orden, 50 por página, PDF/Excel. Columnas:
 *       Devoluciones:         Numero, Fecha, Cliente, Importe, Editar
 *       Devoluciones Inversa: Numero, Fecha, Folio, Cliente, Concepto, Banco, Importe, Editar
 *   - SIN baja/cancelación desde el listado (V1 no la tiene): solo botón Editar.
 *   - Editar (modal chico): Fecha + Importe (+ Folio en Inversa), con confirmación
 *     "Estas seguro? / Se registrará la devolucion".
 *   - Captura ("Captura una Devolucion"):
 *       Devoluciones:         Fecha, Cliente, Banco (todos), Concepto, Importe
 *       Devoluciones Inversa: Fecha, Cliente, Banco (solo MXN), Concepto (+Contenedor),
 *                             Observaciones (NA), Importe, Folio
 */
const KINDS = {
  devolucion: {
    route: 'devoluciones', recurso: 'devoluciones', idField: 'idDevolucion',
    conceptos: ['Bonificacion', 'Descuento', 'Regreso'], soloMXN: false, inversa: false,
    tituloListado: 'Reporte de Devoluciones', btnNuevo: '+ Nueva Devolucion',
    pdfTitulo: 'Listado Devoluciones', pdfFile: 'Devoluciones', excelFile: 'ListadoDevoluciones',
  },
  inversa: {
    route: 'devoluciones-inversa', recurso: 'devoluciones-inversa', idField: 'idDevolucionInversa',
    conceptos: ['Bonificacion', 'Descuento', 'Regreso', 'Contenedor'], soloMXN: true, inversa: true,
    tituloListado: 'Reporte de Devoluciones', btnNuevo: '+ Nueva Devolucion',
    pdfTitulo: 'Listado Devoluciones', pdfFile: 'Devoluciones', excelFile: 'ListadoDevoluciones',
  },
};

function CapturaModal({ open, onClose, k, clientes, bancos, onSaved }) {
  const vacio = { fecha: today(), clientes: '', bancos: '', concepto: '', importe: 0, observaciones: 'NA', folio: '' };
  const [v, setV] = useState(vacio);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { if (open) setV({ ...vacio, fecha: today() }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (key, val) => setV((s) => ({ ...s, [key]: val }));

  const bancosOpts = [...bancos].sort((a, b) => (a.banco > b.banco ? 1 : -1)).filter((b) => (k.soloMXN ? b.moneda === 'MXN' : true));
  const clientesOpts = [...clientes].sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!v.clientes || !v.bancos || !v.concepto || !(Number(v.importe) > 0) || !v.fecha || (k.inversa && !String(v.folio).trim())) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Favor de completar todos los campos' });
      return;
    }
    setGuardando(true);
    try {
      const body = { fecha: v.fecha, importe: Number(v.importe), clientes: v.clientes, concepto: v.concepto, bancos: v.bancos };
      if (k.inversa) { body.observaciones = v.observaciones || 'NA'; body.folio = String(v.folio); }
      await apiClient.post(`/${k.route}`, body);
      await Swal.fire('Good job!', 'Creado con exito', 'success');
      onClose();
      onSaved();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: err.message });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Captura una Devolucion" width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Regresar</button>
          <button type="submit" form="devolucion-form" disabled={guardando} className="btn-primary">
            {guardando ? 'Registrando...' : 'Registrar'}
          </button>
        </>
      )}
    >
      <form id="devolucion-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Fecha</label>
          <input type="date" required value={v.fecha} onChange={(e) => set('fecha', e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label">Cliente</label>
          <select value={v.clientes} onChange={(e) => set('clientes', e.target.value)} className="select-base">
            <option value="">Selecciona</option>
            {clientesOpts.map((c) => <option key={c._id} value={c._id}>{c.codigo} {c.nombre_comercial}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Banco</label>
          <select value={v.bancos} onChange={(e) => set('bancos', e.target.value)} className="select-base">
            <option value="">Selecciona un Banco</option>
            {bancosOpts.map((b) => <option key={b._id} value={b._id}>{b.banco} {b.cuenta}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Concepto</label>
          <select value={v.concepto} onChange={(e) => set('concepto', e.target.value)} className="select-base">
            <option value="">Selecciona un Concepto</option>
            {k.conceptos.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {k.inversa && (
          <div>
            <label className="label">Observaciones</label>
            <input type="text" value={v.observaciones} onChange={(e) => set('observaciones', e.target.value)} className="input-base" />
          </div>
        )}
        <div>
          <label className="label">Importe</label>
          <input type="number" min="0" step="any" value={v.importe} onChange={(e) => set('importe', e.target.value)} className="input-base" />
        </div>
        {k.inversa && (
          <div>
            <label className="label">Folio</label>
            <input type="text" required value={v.folio} onChange={(e) => set('folio', e.target.value)} className="input-base" />
          </div>
        )}
      </form>
    </Modal>
  );
}

function EditarModal({ row, onClose, k, onSaved }) {
  const [fecha, setFecha] = useState(row?.fecha || '');
  const [importe, setImporte] = useState(row?.importe ?? 0);
  const [folio, setFolio] = useState(row?.folio || '');
  useEffect(() => {
    if (row) { setFecha(row.fecha); setImporte(row.importe); setFolio(row.folio || ''); }
  }, [row]);

  async function handleSubmit(e) {
    e.preventDefault();
    const r = await Swal.fire({
      title: 'Estas seguro?', text: 'Se registrará la devolucion', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Si, Enviar!',
    });
    if (!r.isConfirmed) return;
    try {
      const body = { fecha, importe: parseFloat(importe), clientes: row.idClientes, bancos: row.idBancos };
      if (k.inversa) body.folio = String(folio);
      await apiClient.patch(`/${k.route}/${row._id}`, body);
      await Swal.fire('Good job!', 'Actualizado con exito', 'success');
      onClose();
      onSaved();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: err.message });
    }
  }

  return (
    <Modal open={!!row} onClose={onClose} title={`Editar Devolucion ${row?.numero ?? ''} ${row?.clientes ?? ''}`} width="max-w-md"
      footer={<button type="submit" form="devolucion-edit-form" className="btn-primary">Guardar</button>}
    >
      <form id="devolucion-edit-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Fecha</label>
          <input type="date" required value={fecha} onChange={(e) => setFecha(e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label">Importe</label>
          <input type="number" required step="any" placeholder="Importe" value={importe} onChange={(e) => setImporte(e.target.value)} className="input-base" />
        </div>
        {k.inversa && (
          <div>
            <label className="label">Folio</label>
            <input type="text" required placeholder="Folio" value={folio} onChange={(e) => setFolio(e.target.value)} className="input-base" />
          </div>
        )}
      </form>
    </Modal>
  );
}

export default function DevolucionesBase({ kind }) {
  const k = KINDS[kind];
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso(`${k.recurso}:ver`);
  const puedeCrear = hasPermiso(`${k.recurso}:crear`);
  const puedeEditar = hasPermiso(`${k.recurso}:editar`);

  const [filas, setFilas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(daysAgo(30));
  const [selectedFechaFin, setSelectedFechaFin] = useState(today());
  const [selectedCliente, setSelectedCliente] = useState('');
  const [capturaOpen, setCapturaOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);

  useEffect(() => {
    apiClient.get('/clientes', { params: { limit: 500, sortBy: 'nombre_comercial', sortOrder: 'asc' } })
      .then(({ data }) => setClientes(data.data || [])).catch(() => setClientes([]));
    apiClient.get('/bancos', { params: { limit: 500 } })
      .then(({ data }) => setBancos(data.data || [])).catch(() => setBancos([]));
  }, []);

  const cargar = useCallback(async () => {
    if (!selectedFechaInicio || !selectedFechaFin) return;
    setCargando(true);
    setError(null);
    try {
      const { data } = await apiClient.get(`/${k.route}/fechas/${selectedFechaInicio}/${selectedFechaFin}`);
      setFilas((data.data || []).map((a) => {
        const cli = primero(a.clientes);
        const ban = primero(a.bancos);
        return {
          _id: a._id, numero: a[k.idField], fecha: a.fecha,
          clientes: cli?.nombre_comercial || '—', idClientes: cli?._id,
          importe: a.importe, banco: ban ? `${ban.banco || ''} ${ban.cuenta || ''}`.trim() : '—', idBancos: ban?._id,
          folio: a.folio, concepto: a.concepto,
        };
      }));
    } catch (e) {
      setError(e.message);
      setFilas([]);
    } finally {
      setCargando(false);
    }
  }, [k.route, k.idField, selectedFechaInicio, selectedFechaFin]);

  useEffect(() => { cargar(); }, [cargar]);

  const filtradas0 = useMemo(
    () => filas.filter((f) => !selectedCliente || f.idClientes === selectedCliente),
    [filas, selectedCliente]
  );

  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(filtradas0, {
    searchFields: k.inversa ? ['clientes', 'importe', 'numero', 'folio', 'concepto', 'banco'] : ['clientes', 'importe', 'numero'],
    perPage: 50, defaultSortBy: 'numero', defaultSortOrder: 'desc',
  });

  const columnasExport = [
    { key: 'numero', label: 'Numero' },
    { key: 'fecha', label: 'Fecha' },
    { key: 'clientes', label: 'Cliente' },
    { key: 'importe', label: 'Importe', render: (r) => formatMoney(r.importe) },
  ];
  const totalFiltrado = filtradas.reduce((t, r) => t + (Number(r.importe) || 0), 0);
  const ordenadasAsc = [...filtradas].sort((a, b) => Number(a.numero) - Number(b.numero));
  const opcionesPdf = {
    subtitulo: `${selectedFechaInicio} - ${selectedFechaFin}`, fileName: k.pdfFile,
    totalCols: { clientes: 'Total', importe: formatMoney(totalFiltrado) },
  };

  const columnas = [
    { key: 'numero', label: 'Numero', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    ...(k.inversa ? [{ key: 'folio', label: 'Folio', sortable: true }] : []),
    { key: 'clientes', label: 'Cliente', sortable: true },
    ...(k.inversa ? [
      { key: 'concepto', label: 'Concepto', sortable: true },
      { key: 'banco', label: 'Banco', sortable: true },
    ] : []),
    { key: 'importe', label: 'Importe', sortable: true, className: 'text-right', render: (r) => formatMoney(r.importe) },
    {
      key: '__acciones', label: 'Editar', sortable: false, className: 'text-center',
      render: (row) => (puedeEditar ? (
        <button title="Editar" aria-label="Editar" className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50" onClick={() => setEditando(row)}>
          <Pencil size={15} />
        </button>
      ) : null),
    },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">{k.tituloListado}</h1>
        {puedeCrear && <button onClick={() => setCapturaOpen(true)} className="btn-primary">{k.btnNuevo}</button>}
      </div>

      <div className="flex items-center gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="input-base w-64" />
        <button
          type="button" className="btn-outline text-xs px-3 py-1.5"
          onClick={() => exportTablePdf(k.pdfTitulo, columnasExport, ordenadasAsc, opcionesPdf)}
        >
          PDF
        </button>
        <button type="button" className="btn-outline text-xs px-3 py-1.5" onClick={() => setMailOpen(true)}>
          eMail
        </button>
        <button
          type="button" className="btn-outline text-xs px-3 py-1.5"
          onClick={() => exportTableExcel(k.pdfTitulo, columnasExport, ordenadasAsc, { fileName: k.excelFile, sheetName: k.excelFile })}
        >
          Excel
        </button>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Fecha Inicio</label>
          <input type="date" value={selectedFechaInicio} onChange={(e) => { setSelectedFechaInicio(e.target.value); setPage(1); }} className="input-base" />
        </div>
        <div>
          <label className="label">Fecha Fin</label>
          <input type="date" value={selectedFechaFin} onChange={(e) => { setSelectedFechaFin(e.target.value); setPage(1); }} className="input-base" />
        </div>
        <div>
          <label className="label">Cliente</label>
          <select value={selectedCliente} onChange={(e) => { setSelectedCliente(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nombre_comercial}</option>)}
          </select>
        </div>
      </div>

      <DataTable columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor} />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado Devoluciones"
        onEnviar={async (email) => {
          const doc = await buildTablePdf(k.pdfTitulo, columnasExport, ordenadasAsc, opcionesPdf);
          await enviarCorreoAdjunto({
            subject: 'Listado Devoluciones', email, fileName: 'ListadoDevoluciones.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras el listado de Devoluciones.'),
          });
        }}
      />

      <CapturaModal open={capturaOpen} onClose={() => setCapturaOpen(false)} k={k} clientes={clientes} bancos={bancos} onSaved={cargar} />
      <EditarModal row={editando} onClose={() => setEditando(null)} k={k} onSaved={cargar} />
    </div>
  );
}
